<template>
    <div>
        <limitless-page-header :loading="busy_loading" :title="'Produkttyp'" :sub-title="title" :title-id="title_id">
            <template #header-elements>
                <limitless-page-header-button :route="{ name: 'producttypes.list' }" icon="icon-circle-left2">Tillbaka</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div v-if="!initialized && busy_loading" class="content">
            <i class="icon-spinner2 spinner"></i>
        </div>
        <div v-else-if="!not_found && product_type" class="content">
            <ValidationObserver ref="producttypeform">
                <form @submit.prevent="saveProductType">
                    <div class="card">
                        <div class="card-body">
                            <div class="row">
                                <div class="col-lg-6">
                                    <div class="form-group">
                                        <label>Namn</label>
                                        <input type="text" class="form-control" :value="product_type.name" disabled>
                                    </div>
                                </div>
                                <div class="col-lg-6">
                                    <div class="form-group">
                                        <label>Kod</label>
                                        <input type="text" class="form-control" :value="product_type.code" disabled>
                                    </div>
                                </div>
                            </div>

                            <ValidationProvider vid="allowed_shipping_methods" name="Fraktsätt" v-slot="{ errors }">
                                <div class="form-group">
                                    <label>Fraktsätt</label>
                                    <div>
                                        <div
                                            v-for="method in shipping_methods"
                                            :key="'shipping-method-' + method.id"
                                            class="custom-control custom-checkbox custom-control-inline mr-4"
                                        >
                                            <input
                                                type="checkbox"
                                                class="custom-control-input"
                                                :id="'chk-shipping-method-' + method.id"
                                                v-model="product_type.allowed_shipping_methods"
                                                :value="method.id"
                                            >
                                            <label class="custom-control-label" :for="'chk-shipping-method-' + method.id">{{ method.label }}<span v-if="method.hint" class="text-muted ml-1">{{ method.hint }}</span></label>
                                        </div>
                                    </div>
                                    <span class="validation-invalid-label">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>

                            <limitless-tabs :tabs="language_tabs" class="mb-3">
                                <template v-for="lang in language_tabs" v-slot:[lang.slot]>
                                    <div :key="'tab-' + lang.id">
                                        <ValidationProvider :vid="'translations.' + lang.id + '.delivery_info'" name="Leveransinfo" v-slot="{ errors }">
                                            <div class="form-group">
                                                <label>Leveransinfo</label>
                                                <simple-text-editor
                                                    v-model="product_type.translations[lang.id].delivery_info"
                                                    :toolbar="[
                                                        'h2',
                                                        'h3',
                                                        'bold',
                                                        'italic',
                                                        'strike',
                                                        'underline',
                                                        'textalign',
                                                        'rawmode',
                                                    ]"
                                                ></simple-text-editor>
                                                <span class="validation-invalid-label">{{ errors[0] }}</span>
                                            </div>
                                        </ValidationProvider>
                                    </div>
                                </template>
                            </limitless-tabs>
                        </div>
                        <div class="card-footer text-right">
                            <limitless-button ref="btnsaveproducttype" type="button" @click="saveProductType" icon="icon-floppy-disk" :busy="busy_saving">Spara</limitless-button>
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </div>
        <div v-else-if="not_found" class="content">
            <i>Produkttypen hittades inte</i>
        </div>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import getError from '@services/ErrorHelper';
import BackendRepository from '@services/repositories/BackendRepository';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import LimitlessTabs from '@components/Limitless/LimitlessTabs.vue';
import SimpleTextEditor from '@components/SimpleTextEditor.vue';

export default {
    name: 'ProductTypeEdit',
    components: {
        LimitlessTabs,
        SimpleTextEditor,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
    },
    props: {
        id: {
            type: [Number, String],
            required: true,
        },
    },
    data: () => ({
        not_found: false,
        initialized: false,
        busy_loading: true,
        busy_saving: false,
        product_type: null,
        languages: [],
        shipping_methods: [
            { id: 'DPD', label: 'MyPack Home', hint: 'P17+PUK' },
            { id: 'postoffice', label: 'MyPack Collect', hint: 'P19' },
            { id: 'postal', label: 'Varubrev', hint: 'PUA' },
            { id: 'online', label: 'Online', hint: '' },
        ],
    }),
    mounted() {
        this.getLanguages();
        this.getProductType();
    },
    watch: {
        id() {
            this.getProductType();
        },
    },
    methods: {
        getProductType() {
            this.busy_loading = true;
            this.not_found = false;
            apiClient.get('/vue/product_types/' + this.id)
                .then((response) => {
                    this.product_type = response.data;
                    this.product_type.allowed_shipping_methods = this.product_type.allowed_shipping_methods || [];
                    this.ensureTranslations();
                })
                .catch((error) => {
                    const { status } = error.response;
                    if (status === 404) {
                        this.not_found = true;
                    }
                })
                .finally(() => {
                    this.initialized = true;
                    this.busy_loading = false;
                });
        },
        async saveProductType() {
            const isValid = await this.$refs.producttypeform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/product_types/' + this.id, {
                    allowed_shipping_methods: this.product_type.allowed_shipping_methods,
                    translations: this.product_type.translations,
                })
                    .then((response) => {
                        this.product_type = response.data;
                        this.product_type.allowed_shipping_methods = this.product_type.allowed_shipping_methods || [];
                        this.ensureTranslations();
                        this.$refs.btnsaveproducttype.triggerSaved();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.producttypeform.setErrors(parsederror);
                        }
                        this.$refs.btnsaveproducttype.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            } else {
                this.$refs.btnsaveproducttype.triggerFailed();
            }
        },
        getLanguages() {
            BackendRepository.getLanguages().then((res) => {
                this.languages = res;
                this.ensureTranslations();
            });
        },
        ensureTranslations() {
            if (!this.product_type || !this.languages.length) {
                return;
            }
            if (!this.product_type.translations) {
                this.$set(this.product_type, 'translations', {});
            }

            this.languages.forEach((lang) => {
                if (!this.product_type.translations[lang.short]) {
                    this.$set(this.product_type.translations, lang.short, {
                        prod_type_id: this.product_type.id,
                        langcode: lang.short,
                        delivery_info: '',
                    });
                }
            });
        },
    },
    computed: {
        title() {
            return this.product_type ? this.product_type.name : '';
        },
        title_id() {
            return this.product_type ? this.product_type.id : '';
        },
        language_tabs() {
            if (!this.product_type) {
                return [];
            }

            return this.languages.map((lang) => ({
                id: lang.short,
                label: lang.name,
                slot: 'slot-' + lang.short,
            }));
        },
    },
};
</script>
